import React, { useState, useRef, useEffect } from "react";
import { DatePicker } from "./DatePicker";
import { PriorityPicker } from "./PriorityPicker";
import { RecurrencePicker } from "./RecurrencePicker";

export function TaskToolbar({
  dueDate,
  onChangeDate,
  priority,
  onChangePriority,
  assigneeUid,
  onChangeAssignee,
  users = [],
  onOpenLabels,
  recurrence = null,
  onChangeRecurrence
}) {
  return (
    <div className="flex flex-wrap items-center gap-2 py-1.5 px-1 overflow-visible">
      <DatePicker value={dueDate} onChange={onChangeDate} />
      <PriorityPicker value={priority} onChange={onChangePriority} />
      <RecurrencePicker value={recurrence} onChange={onChangeRecurrence} />
      <AssigneePicker assigneeUid={assigneeUid} onChangeAssignee={onChangeAssignee} users={users} />
      
      <button type="button" className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border border-gray-200 text-xs font-semibold text-gray-400 hover:text-gray-500 hover:bg-gray-50 transition-all duration-150 cursor-pointer" title="Reminders (Decorative)">
        <span>⏰</span><span>Reminders</span>
      </button>

      <button type="button" className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border border-gray-200 text-xs font-semibold text-gray-400 hover:text-gray-500 hover:bg-gray-50 transition-all duration-150 cursor-pointer" title="Attachment (Decorative)">
        <span>📎</span><span>Attachment</span>
      </button>

      <MoreOptions onOpenLabels={onOpenLabels} />
    </div>
  );
}

function AssigneePicker({ assigneeUid, onChangeAssignee, users = [] }) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const selectedUser = users.find((u) => u.uid === assigneeUid);
  // Only show active users in assignee list; include current assignee if already assigned
  const selectableUsers = users.filter((u) =>
    (u.status || "Active") === "Active" || u.uid === assigneeUid
  );
  const filteredUsers = selectableUsers.filter(
    (u) => u.name.toLowerCase().includes(search.toLowerCase()) || u.email.toLowerCase().includes(search.toLowerCase())
  );

  const handleClear = (e) => {
    e.stopPropagation();
    onChangeAssignee("");
    setIsOpen(false);
  };

  const pillText = selectedUser
    ? (selectedUser.status === "Inactive" ? `${selectedUser.name} (Inactive)` : selectedUser.name)
    : "Assignee";
  const pillClass = selectedUser ? "text-blue-600 bg-blue-50 border-blue-200 hover:bg-blue-100" : "text-gray-500 hover:bg-gray-100 border-gray-300";
  const iconColor = selectedUser ? "text-blue-500" : "text-gray-400";

  return (
    <div ref={containerRef} className="relative inline-block text-left">
      <button type="button" onClick={() => setIsOpen(!isOpen)} className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border text-xs font-semibold transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-gray-200 ${pillClass}`}>
        <span className={iconColor}>👤</span>
        <span>{pillText}</span>
        {selectedUser && <span onClick={handleClear} className="ml-1 text-xs hover:bg-black/10 rounded-full w-4 h-4 flex items-center justify-center font-bold text-gray-500 transition-colors">×</span>}
      </button>

      {isOpen && (
        <div className="absolute z-50 mt-1 left-0 w-56 bg-white border border-gray-200 rounded-lg shadow-lg py-1 flex flex-col max-h-60 animate-fadeIn">
          <div className="p-2 border-b border-gray-100">
            <input type="text" placeholder="Search people..." value={search} onChange={(e) => setSearch(e.target.value)} className="w-full text-xs px-2.5 py-1.5 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500 text-gray-700" />
          </div>
          <div className="overflow-y-auto flex-1">
            {selectedUser && (
              <button type="button" onClick={() => { onChangeAssignee(""); setIsOpen(false); }} className="w-full text-left px-3 py-2 text-xs font-bold text-red-500 hover:bg-red-50">❌ Unassign</button>
            )}
            {filteredUsers.length === 0 ? (
              <div className="px-3 py-2.5 text-xs text-gray-400 italic text-center">No users found</div>
            ) : (
              filteredUsers.map((user) => (
                <button key={user.uid} type="button" onClick={() => { onChangeAssignee(user.uid); setIsOpen(false); }} className="w-full text-left px-3 py-2 text-xs text-gray-700 hover:bg-gray-50 flex flex-col">
                  <span className="font-semibold flex items-center justify-between">
                    <span>{user.name}</span>
                    {user.status === "Inactive" && <span className="text-[10px] text-gray-400 font-normal italic">(Inactive)</span>}
                  </span>
                  <span className="text-[9px] text-gray-400">{user.email}</span>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function MoreOptions({ onOpenLabels }) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  return (
    <div ref={containerRef} className="relative inline-block text-left">
      <button type="button" onClick={() => setIsOpen(!isOpen)} className="inline-flex items-center justify-center w-7 h-7 rounded-full border border-gray-300 text-xs font-bold text-gray-500 hover:bg-gray-100 transition-all duration-150 focus:outline-none">···</button>
      {isOpen && (
        <div className="absolute z-50 mt-1 left-0 w-36 bg-white border border-gray-200 rounded-lg shadow-lg py-1 animate-fadeIn">
          <button type="button" onClick={() => { onOpenLabels(); setIsOpen(false); }} className="w-full text-left px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50 flex items-center gap-2">
            <span>♡</span><span>Labels</span>
          </button>
          <button type="button" className="w-full text-left px-3 py-2 text-xs font-medium text-gray-400 cursor-not-allowed flex items-center gap-2" title="Location (Decorative)" disabled>
            <span>🌍</span><span>Location</span>
          </button>
        </div>
      )}
    </div>
  );
}
